// The models an ANSWER may pin (#6521) — the picker's offers, folded onto `Select`.
//
// A blocker answer may name the model the TRIAGE JUDGE decides its ask on. The choice is
// limited to what the account's own keys serve on the triage LANE, which is exactly what
// `modelPickerOptions` already computes for the Agents page's Triage card
// (`laneForPinPurpose` sends `triage` to `general`) — so the two offer the same models
// and a person cannot pick on one screen what the other would refuse.
//
// NOT `ModelPicker`: that component's props are a STORED `llm_routes` row (`route`,
// `pinValue`) and its `onChoose` emits a `RouteChange`. An answer-time pick writes no
// row, so this is the themed `Select` over the same offers instead.

import {
  BYOKProviderSchema,
  type ModelPickerInput,
  type ModelPickerOption,
  modelPickerOptions,
  type TaskLlmPreference,
  TRIAGE_PIN_PURPOSE,
} from '@developerz/domain';
import { t } from '../i18n';
import type { KeyRow } from './keys-types';

/** The `Select` value meaning "no pin" — the account's own route decides. */
export const ANSWER_MODEL_AUTOMATIC = '';

/** One option on the answer form's model field. */
export type AnswerModelOption = {
  readonly value: string;
  readonly label: string;
  readonly hint?: string;
  readonly disabled?: boolean;
};

/**
 * The offers a person may pin on an answer, in the picker's own display order, led by
 * Automatic. A `(provider, model)` pair is the value, joined by `\u0000` — the same
 * separator `modelPickerOptions` groups pairs on, and a character neither a provider nor
 * a model may contain.
 *
 * An UNAVAILABLE offer (an unverified or invalid key's model) is listed `disabled`, as
 * the Agents picker lists it: a person who sees their key's model greyed out learns the
 * key needs attention, where omitting it would read as "we lost your key".
 */
export function answerModelOptions(keys: readonly KeyRow[]): AnswerModelOption[] {
  return modelSelectOptions(
    keys,
    { purpose: TRIAGE_PIN_PURPOSE, currentPin: null },
    {
      automatic: t('blockers.answer_model_automatic'),
      unavailable: t('blockers.answer_model_unavailable'),
    },
  );
}

/** The two words a picker's caller names: its Automatic row and an unavailable offer's hint. */
export type ModelSelectWords = { readonly automatic: string; readonly unavailable: string };

/**
 * Any purpose's offers on `Select`, led by Automatic (#6753 generalized this by purpose: the
 * card's epic picker asks for `general`, an answer for `triage`). A stored pin the keys still
 * serve is listed first after Automatic (`modelPickerOptions`' `pinned`), so it stays selected.
 */
export function modelSelectOptions(
  keys: readonly KeyRow[],
  input: ModelPickerInput,
  words: ModelSelectWords,
): AnswerModelOption[] {
  const offers = modelPickerOptions(keys, input);
  return [
    { value: ANSWER_MODEL_AUTOMATIC, label: words.automatic },
    ...offers.flatMap((o) => optionOf(o, words)),
  ];
}

function offerOption(
  offer: Extract<ModelPickerOption, { kind: 'model' | 'unavailable' }>,
  words: ModelSelectWords,
): AnswerModelOption {
  return offer.kind === 'model'
    ? {
        value: pinValueOf(offer.provider, offer.model),
        label: offer.displayName,
        hint: offer.provider,
      }
    : {
        value: pinValueOf(offer.provider, offer.model),
        label: offer.displayName,
        hint: words.unavailable,
        disabled: true,
      };
}

function optionOf(offer: ModelPickerOption, words: ModelSelectWords): AnswerModelOption[] {
  if (offer.kind === 'model' || offer.kind === 'unavailable') return [offerOption(offer, words)];
  if (offer.kind === 'pinned') {
    // The stored pin: listed (never disabled) so the picker can show it selected.
    if (!offer.gone) return [{ ...offerOption(offer.offer, words), disabled: false }];
    return [
      {
        value: pinValueOf(offer.provider ?? '', offer.model),
        label: offer.displayName,
        hint: words.unavailable,
      },
    ];
  }
  // `automatic` is led above; `empty` is the no-offers marker the form reads from the
  // list's own length.
  return [];
}

/** The `(provider, model)` pair as one `Select` value. */
export function pinValueOf(provider: string, model: string): string {
  return `${provider}\u0000${model}`;
}

/**
 * A `Select` value back to the pin the answer door takes; `null` for Automatic.
 *
 * The provider half is PARSED, never cast: the value round-trips through the DOM, and a
 * pin naming a provider the platform does not serve is one the door would refuse — so an
 * unreadable value answers Automatic rather than sending a pin nothing can honour.
 */
export function answerPinOf(value: string): TaskLlmPreference | null {
  if (value === ANSWER_MODEL_AUTOMATIC) return null;
  const [provider, model] = value.split('\u0000');
  const parsed = BYOKProviderSchema.safeParse(provider);
  return parsed.success && model !== undefined && model !== ''
    ? { provider: parsed.data, model }
    : null;
}
